<template>
  <section
    v-if="proposals.length > 0 || shouldShowResponsible"
    class="is-card"
  >
    <h2 class="is-bolder font-md">
      Propostas FGTS
    </h2>
    <p>
      As propostas abaixo já estão em uma etapa avançada. Revise a pendência para conclui-lá com
      sucesso.
    </p>
    <span class="font-sm">
      <strong class="is-bold">
        {{ proposals.length }} proposta {{ proposals.length > 1 ? 's' : '' }}
      </strong>
      averbadas com pendência
    </span>

    <template
      v-if="shouldShowResponsible"
    >
      <base-select
        v-model="form.responsible_id"
        class="responsible-select"
        :options="list.responsible"
        :searching="searching['responsible']"
        can-filter
        has-api-search
        @search="handleResponsibleSearch(
          $event,
          { ...(promoter_id ? { promoter_id } : '') },
          'responsible',
        )"
        @input="getSimplifiedProposalTable"
      >
        Responsável
      </base-select>
    </template>

    <div>
      <table
        v-if="proposals.length > 0"
        class="table"
      >
        <thead>
          <tr>
            <th class="left-aligned">
              Cliente
            </th>
            <th class="left-aligned">
              Status
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(proposal) in proposals"
            :key="proposal.proposal_id"
          >
            <td
              :data-tooltip="`Responsável: ${proposal.responsible_name}`"
              class="left-aligned"
            >
              <p class="is-semi-bold is-highlighted">
                {{ proposal.document }}
              </p>
              <p>{{ proposal.name }}</p>
            </td>
            <td
              :data-tooltip="`${proposal.status}${proposal.obs ? ' - ' + proposal.obs : ''}`"
              class="left-aligned"
            >
              <span
                class="tag"
                :class="proposalStatusCypher[proposal.status]?.tag"
              >
                {{ proposal.status }}
              </span>
            </td>
            <td class="table-action">
              <button
                class="button"
                @click="openProposalList(proposal.proposal_id)"
              >
                <LinkIcon class="action__icon" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div
        v-else
        class="empty-state"
      >
        <p class="is-bold">
          Não encontramos nenhum resultado para essa solicitação.
        </p>
      </div>
      <ThePagination
        :data="pagination.data"
        :storage-key="pagination.filterKey"
        @paginate="getSimplifiedProposalTable"
      />
    </div>
  </section>
</template>
<script>

import {
  proposals as proposalsAPI,
  accounts as accountsAPI,
} from '@/services';

import config from '@/data/config';

import roleManagerMixin from '@/mixins/roleManager.mixin';
import promoterPaginationMixin from '@/mixins/promoterPagination.mixin';
import loadingMixin from '@/mixins/loadingView.mixin';

import { cypher as proposalStatusCypher } from '@/data/proposalStatusCypher.js';
import LinkIcon from '@/assets/icons/Link.vue';

import ThePagination from '@/components/ThePagination.vue';
import BaseSelect from '@/components/Base/Select/index.vue';

export default {
  name: 'SimplifiedProposalTable',
  components: {
    LinkIcon,
    ThePagination,
    BaseSelect,
  },
  mixins: [
    promoterPaginationMixin,
    loadingMixin,
    roleManagerMixin,
  ],
  data() {
    return {
      pagination: {
        data: {},
        filterKey: 'hub',
      },
      form: {
        responsible_id: '',
      },
      list: {
        responsible: [],
      },
      proposals: [],
      proposalStatusCypher,
    };
  },
  computed: {
    responsible_id() {
      return this.form.responsible_id;
    },
    shouldShowResponsible() {
      return [
        this.isFinancial,
        this.isSuper,
        this.isManager,
        this.isCommercial,
        this.isCoordenador,
        this.isAdmin,
      ].some((isValid) => isValid);
    },
  },
  mounted() {
    this.getSimplifiedProposalTable();
    this.getAllResponsibles();
  },
  methods: {
    // handlers
    handlerError(err) {
      let message = err?.response?.data?.error?.message
        || (err?.error?.response?.data?.error?.message
        || err?.error?.data?.message
        || err?.message
        || err);

      if (err?.response?.data?.error?.errors) {
        const errors = Object.values(err?.response?.data?.error?.errors);

        message = message.concat(`<br> - ${errors.map((error) => error).join('<br> - ')}`);
      }

      return message;
    },
    openProposalList(proposal_id) {
      sessionStorage.setItem('filters', JSON.stringify(
        {
          proposal_id,
        },
      ));

      const { href } = this.$router.resolve({ name: 'fgts-proposal' });
      window.open(href, '_blank');
    },

    // API
    async getSimplifiedProposalTable() {
      this.pagination.data = {};
      this.proposals = [];
      this.loading = true;

      await proposalsAPI
        .getProposals({
          responsible_id: this.responsible_id ? this.responsible_id : null,
          product_id: config.PRODUCT_ID_FGTS,
          status: 'Pendência',
          last_day: true,
        })
        .then(({ data }) => {
          this.pagination.data = data;
          this.proposals = data.data;
        })
        .catch((err) => this.handlerError(err))
        .finally(() => {
          this.loading = false;
        });
    },
    getAllResponsibles() {
      this.loading = true;

      accountsAPI
        .searchResponsible()
        .then(({ data }) => {
          this.list.responsible = data.data.data.map((elem) => ({
            value: elem.account_id,
            name: elem.name,
          }));
        })
        .catch((err) => this.handlerError(err))
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';

.empty-state {
  p {
    text-align: center;
  }
}
.responsible-select {
  margin: 32px 0;
}
p {
  margin: 7px 0;
}
span {
  margin: 31px 0 0 0;
  display: inline-block;
}
.table {
  border: 1px solid $gray-200;
  border-radius: 8px;
  border-collapse: separate;
  width: 100%;

  p, span {
    margin: 0;
  }

  tr {
    &:nth-child(even) td{
      background-color: $gray-50;
    }
    &.is-checked {
      background-color: $prata-green-50;
    }

    &--header {
      color: map.get($stone-types, "500");
    }

    &--content {
      color: map.get($stone-types, "800");
    }

    th, td {
      &.left-aligned {
        text-align: left;
      }

      &:first-child {
        padding: 8px 0px 8px 16px;
      }
      &:last-child {
        padding: 8px 16px 8px 0px;
      }

      padding: 8px;
      vertical-align: middle;
      width: 25%;
      text-align: center;
      color: map.get($stone-types, "500");
      text-wrap: nowrap;
      &:first-of-type {
        width: 62px;
      }

      &.small-column {
        width: 14%;
      }

      p {
        color: map.get($stone-types, "500");

        &.is-highlighted {
          color: map.get($stone-types, "900");
        }
      }
    }

    th {
      border-bottom: 1px solid map.get($stone-types, "200");
      background-color: map.get($stone-types, "50");
      text-transform: uppercase;
      letter-spacing: 0.05rem;
      text-align: center;
    }

    td {
      border: none;
      line-height: 20px;
      letter-spacing: 0%;
    }

    .table-action {
      button {
        margin: 0 4px;
        padding: 7px;
        width: 30px;
        max-height: 30px;

        &::v-deep {
          svg {
            min-width: 12px;
            min-height: 12px;
          }
        }
      }
    }
  }
}
@media only screen and (min-width: 1024px) {

  .table {
    tr {
      th, td {
        padding: 8px 32px;
      }
    }
  }
}
</style>
